import {useEffect} from "react";
import {IOrganiser, Roles} from "@evennt-tech/api-v2-core";
import {useOrganisers} from "store/organisers/organiser.store";
import {shallow} from "zustand/shallow";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";

type UseOrganiserResult = [IOrganiser | null, boolean, string | null];

const useOrganiser = (id?: string): UseOrganiserResult => {
  const {
    selected,
    isLoading,
    error,
    fetchOne,
    fetchOneByUserId,
  } = useOrganisers(
    (state) => ({
      selected: state.selected,
      isLoading: state.isLoading,
      error: state.error,
      fetchOne: state.fetchOne,
      fetchOneByUserId: state.fetchOneByUserId,
    }),
    shallow
  );

  const {role, id: userId} = useSelector(selectUser);

  useEffect(() => {
    const organiserId = id || userId;
    if (!organiserId) return;

    // Reuse the store's `selected` when it already holds the requested
    // organiser: consumers of this hook mount inside the task drawer
    // (TaskSettings, InputFileMaxSize), so without the guard every drawer
    // open refired the same by-user-id request.
    const {selected: current} = useOrganisers.getState();
    if (id) {
      if (current?.id === id) return;
      fetchOne({organiserId});
    } else if (role === Roles.ORGANISER) {
      if (current?.userId === userId) return;
      fetchOneByUserId({userId: organiserId});
    }
  }, [id, role, userId]);

  return [selected, isLoading, error];
};

export default useOrganiser;
